import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@web/core/query';
import { travelerAccessRepository, type TravelerAccessFilter } from '@web/data/repositories';
import { t } from '@web/i18n';
import { Button, Input } from '@web/ui/core/atoms';
import { useOrgContext } from '@web/ui/core/hooks';
import { EmptyHint, PageSectionHead } from '@web/ui/core/molecules';
import { TabBar } from '@web/ui/core/organisms';

import { EventsTable, GrantsTable } from '../components/access_tables';

type Mode = 'traveler' | 'operator';

const PLACEHOLDER = {
  traveler: 'travelerAccess.search.traveler',
  operator: 'travelerAccess.search.operator',
} as const;

function toFilter(mode: Mode, id: string): TravelerAccessFilter {
  return mode === 'traveler' ? { travelerPublicId: id } : { operatorId: id };
}

/**
 * "Ver como viajante" audit trail (spec 2026-10-12): who viewed a traveler, or
 * what an operator viewed. Exact-id lookup only — the auditor already holds the
 * traveler's publicId or the operator's id; the trail itself is not searchable.
 */
export function TravelerAccessPage(): React.JSX.Element {
  const { orgId } = useOrgContext();
  const [mode, setMode] = useState<Mode>('traveler');
  const [draft, setDraft] = useState('');
  const [filter, setFilter] = useState<TravelerAccessFilter | null>(null);
  const trail = useQuery({
    queryKey: queryKeys.travelerAccess.list(orgId, filter ?? {}),
    queryFn: () => travelerAccessRepository.list(orgId, filter as TravelerAccessFilter),
    enabled: filter !== null,
  });

  const switchTo = (next: Mode): void => {
    setMode(next);
    setDraft('');
    setFilter(null);
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageSectionHead title={t('travelerAccess.title')} sub={t('travelerAccess.sub')} />
      <TabBar ariaLabel={t('travelerAccess.tabs')}>
        <TabBar.Tab
          icon="user"
          label={t('travelerAccess.tab.byTraveler')}
          isActive={mode === 'traveler'}
          onClick={() => {
            switchTo('traveler');
          }}
        />
        <TabBar.Tab
          icon="headset"
          label={t('travelerAccess.tab.byOperator')}
          isActive={mode === 'operator'}
          onClick={() => {
            switchTo('operator');
          }}
        />
      </TabBar>
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          const id = draft.trim();
          setFilter(id ? toFilter(mode, id) : null);
        }}
      >
        <Input
          type="search"
          aria-label={t(PLACEHOLDER[mode])}
          placeholder={t(PLACEHOLDER[mode])}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
        />
        <Button type="submit" variant="primary">
          {t('travelerAccess.search.submit')}
        </Button>
      </form>
      {trail.data ? (
        <>
          {/* A failed refetch (e.g. on reconnect) keeps the cached trail on screen. */}
          {trail.isError ? <EmptyHint>{t('travelerAccess.error')}</EmptyHint> : null}
          <section className="flex flex-col gap-2">
            <h2 className="m-0 text-base font-medium text-foreground">
              {t('travelerAccess.grants.title')}
            </h2>
            <GrantsTable grants={trail.data.grants} />
          </section>
          <section className="flex flex-col gap-2">
            <h2 className="m-0 text-base font-medium text-foreground">
              {t('travelerAccess.events.title')}
            </h2>
            <EventsTable events={trail.data.events} />
          </section>
        </>
      ) : trail.isError ? (
        <EmptyHint>{t('travelerAccess.error')}</EmptyHint>
      ) : trail.isFetching ? (
        <EmptyHint>{t('travelerAccess.loading')}</EmptyHint>
      ) : (
        <EmptyHint>{t('travelerAccess.idle')}</EmptyHint>
      )}
    </div>
  );
}
